import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

const PIECE = {
  Cube: 1,
  Stone: 2,
  Jar: 3,
  Cone: 4,
  Metal: 5,
  Ice: 6,
  Tree: 7,
  Watermelon: 8,
  Plastic: 9,
  RoundMetal: 10,
  IronCan: 11,
  Column: 12,
  RoundColumn: 13,
} as const;

const SUPER_HARD = 2;

type Filler = ( x: number, y: number, layer: number, id: number ) => { type: number; variant?: number };

interface StressSpec
{
  name: string;
  columns: number;
  rows: number;
  layers: number;
  fill: Filler;
  /** Footprint of every piece in cells; 1x1 when omitted. */
  piece?: { width: number; height: number };
  /**
   * Upright 1xN pieces stacked per grid column instead of a fixed footprint:
   * heights cycle through this pattern (offset by column and layer), the top
   * piece is cut to fit the rows left.
   */
  stack?: number[];
  /** Groups a series of specs for its own debug section. */
  group?: "column" | "can";
}

const TOP_ROW = 0;

const cone = ( id: number ) => ( { type: PIECE.Cone, variant: 3 + id % 8 } );

const cubes: Filler = ( x, y, layer, id ) => ( { type: PIECE.Cube, variant: ( id + layer ) % 3 } );

const mixed: Filler = ( x, y, layer, id ) =>
{
  if ( y === TOP_ROW && ( x + layer ) % 3 === 0 )
    return cone( id );

  switch ( ( x + y * 3 + layer * 5 ) % 8 )
  {
    case 0: return { type: PIECE.Cube, variant: 1 };
    case 1: return { type: PIECE.Stone };
    case 2: return { type: PIECE.Plastic, variant: ( id + layer ) % 8 };
    case 3: return { type: PIECE.Jar, variant: id % 4 };
    case 4: return { type: PIECE.Metal, variant: id % 7 };
    case 5: return { type: PIECE.Ice };
    case 6: return { type: PIECE.Tree };
    default: return { type: PIECE.Cube, variant: 2 };
  }
};

const everything: Filler = ( x, y, layer, id ) =>
{
  if ( y === TOP_ROW && ( x + layer ) % 3 === 1 )
    return cone( id );

  switch ( ( x * 2 + y + layer * 3 ) % 11 )
  {
    case 0: return { type: PIECE.Cube, variant: 0 };
    case 1: return { type: PIECE.Cube, variant: 1 };
    case 2: return { type: PIECE.Cube, variant: 2 };
    case 3: return { type: PIECE.Stone };
    case 4: return { type: PIECE.Plastic, variant: id % 8 };
    case 5: return { type: PIECE.Jar, variant: id % 4 };
    case 6: return { type: PIECE.Metal, variant: id % 7 };
    case 7: return { type: PIECE.RoundMetal, variant: id % 7 };
    case 8: return { type: PIECE.IronCan, variant: id % 2 };
    case 9: return { type: PIECE.Ice };
    default: return { type: PIECE.Watermelon };
  }
};

// Cube variants as GameScreen maps them: 0 → purple plywood, 1 → brick (BrickMasonry), 2 → green plywood.
const bricks: Filler = () => ( { type: PIECE.Cube, variant: 1 } );
const plywood: Filler = () => ( { type: PIECE.Cube, variant: 0 } );

const squareColumns: Filler = () => ( { type: PIECE.Column } );
const roundColumns: Filler = () => ( { type: PIECE.RoundColumn } );
const mixedColumns: Filler = ( x, y, layer ) => ( { type: ( x + y + layer ) % 2 === 0 ? PIECE.Column : PIECE.RoundColumn } );

// Cans: square and round cycle all 7 colours, iron its 2 skins.
const squareCans: Filler = ( x, y, layer, id ) => ( { type: PIECE.Metal, variant: id % 7 } );
const roundCans: Filler = ( x, y, layer, id ) => ( { type: PIECE.RoundMetal, variant: id % 7 } );
const ironCans: Filler = ( x, y, layer, id ) => ( { type: PIECE.IronCan, variant: id % 2 } );
const mixedCans: Filler = ( x, y, layer, id ) =>
{
  switch ( ( x + y + layer ) % 3 )
  {
    case 0: return squareCans( x, y, layer, id );
    case 1: return roundCans( x, y, layer, id );
    default: return ironCans( x, y, layer, id );
  }
};

interface StressTier
{
  tier: string;
  columns: number;
  rows: number;
  layers: number;
  stack: number[];
}

// By difficulty: EASY = few tall pieces, EXTREME = deep grid of short ones.
const COLUMN_TIERS: StressTier[] = [
  { tier: "EASY",    columns: 9, rows: 4, layers: 2,  stack: [ 4 ] },
  { tier: "MEDIUM",  columns: 9, rows: 8, layers: 4,  stack: [ 4, 2, 2 ] },
  { tier: "HARD",    columns: 9, rows: 8, layers: 6,  stack: [ 1, 2, 3, 4 ] },
  { tier: "EXTREME", columns: 9, rows: 8, layers: 10, stack: [ 1, 1, 2, 1, 3 ] },
];

// Cans come in 1x1-1x3 only.
const CAN_TIERS: StressTier[] = [
  { tier: "EASY",    columns: 9, rows: 3, layers: 2,  stack: [ 3 ] },
  { tier: "MEDIUM",  columns: 9, rows: 8, layers: 4,  stack: [ 3, 2, 3 ] },
  { tier: "HARD",    columns: 9, rows: 8, layers: 6,  stack: [ 1, 2, 3 ] },
  { tier: "EXTREME", columns: 9, rows: 8, layers: 10, stack: [ 1, 1, 2, 1, 3 ] },
];

function stressSeries(
  group: NonNullable<StressSpec["group"]>,
  tiers: StressTier[],
  series: ReadonlyArray<readonly [ string, Filler ]>
): StressSpec[]
{
  return series.flatMap( ( [ label, fill ] ) => tiers.map( tier => ( {
    name: `${label} ${tier.tier} ${tier.columns}x${tier.rows} x${tier.layers}`,
    columns: tier.columns,
    rows: tier.rows,
    layers: tier.layers,
    stack: tier.stack,
    fill,
    group,
  } ) ) );
}

const COLUMN_SPECS = stressSeries( "column", COLUMN_TIERS, [
  [ "COL SQUARE", squareColumns ], [ "COL ROUND", roundColumns ], [ "COL MIX", mixedColumns ],
] );

const CAN_SPECS = stressSeries( "can", CAN_TIERS, [
  [ "CAN SQUARE", squareCans ], [ "CAN ROUND", roundCans ], [ "CAN IRON", ironCans ], [ "CAN MIX", mixedCans ],
] );

const SPECS: StressSpec[] = [
  { name: "WALL 9x6 x4",     columns: 9, rows: 6, layers: 4, fill: cubes },
  { name: "MIX 9x6 x5",      columns: 9, rows: 6, layers: 5, fill: mixed },
  { name: "TOWER 8x8 x4",    columns: 8, rows: 8, layers: 4, fill: mixed },
  { name: "DEPTH 9x5 x8",    columns: 9, rows: 5, layers: 8, fill: mixed },
  { name: "EVERYTHING 9x7 x6", columns: 9, rows: 7, layers: 6, fill: everything },
  // Brick vs default block pairs: same grid, same footprint, only the skin differs.
  { name: "BRICK 1x1 9x6 x4", columns: 9, rows: 6, layers: 4, fill: bricks },
  { name: "PLY 1x1 9x6 x4",   columns: 9, rows: 6, layers: 4, fill: plywood },
  { name: "BRICK 3x1 9x6 x4", columns: 9, rows: 6, layers: 4, fill: bricks, piece: { width: 3, height: 1 } },
  { name: "PLY 3x1 9x6 x4",   columns: 9, rows: 6, layers: 4, fill: plywood, piece: { width: 3, height: 1 } },
  { name: "BRICK 2x2 8x6 x4", columns: 8, rows: 6, layers: 4, fill: bricks, piece: { width: 2, height: 2 } },
  { name: "PLY 2x2 8x6 x4",   columns: 8, rows: 6, layers: 4, fill: plywood, piece: { width: 2, height: 2 } },
  ...COLUMN_SPECS,
  ...CAN_SPECS,
];

function buildStackLayer( spec: StressSpec, stack: number[], layer_index: number ): LevelLayer
{
  const grid: number[][] = Array.from( { length: spec.rows }, () => Array( spec.columns ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let x = 0; x < spec.columns; x++ )
  {
    // Fill from the floor up; y counts from the top row.
    let bottom = spec.rows;
    for ( let k = 0; bottom > 0; k++ )
    {
      const height = Math.min( stack[( x + layer_index + k ) % stack.length], bottom );
      const y = bottom - height;
      const visual = spec.fill( x, y, layer_index, id );

      for ( let row = y; row < bottom; row++ )
        grid[row][x] = id;

      pieces.push( { id, x, y, width: 1, height, type: visual.type as LevelPieceType, variant: visual.variant } );
      id++;
      bottom = y;
    }
  }

  return { grid, pieces };
}

function buildLayer( spec: StressSpec, layer_index: number ): LevelLayer
{
  if ( spec.stack )
    return buildStackLayer( spec, spec.stack, layer_index );

  const grid: number[][] = Array.from( { length: spec.rows }, () => Array( spec.columns ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  const width = spec.piece?.width ?? 1;
  const height = spec.piece?.height ?? 1;
  let id = 1;

  for ( let y = 0; y + height <= spec.rows; y += height )
  {
    for ( let x = 0; x + width <= spec.columns; x += width )
    {
      const visual = spec.fill( x, y, layer_index, id );

      for ( let row = y; row < y + height; row++ )
        for ( let col = x; col < x + width; col++ )
          grid[row][col] = id;

      pieces.push( {
        id,
        x,
        y,
        width,
        height,
        type: visual.type as LevelPieceType,
        variant: visual.variant,
      } );
      id++;
    }
  }

  return { grid, pieces };
}

function buildConfig( index: number ): LevelConfig
{
  const spec = SPECS[index];

  return {
    level: StressTest.BASE_LEVEL + index,
    difficulty: SUPER_HARD as LevelConfig["difficulty"],
    layers: Array.from( { length: spec.layers }, ( _, i ) => buildLayer( spec, i ) ),
  };
}

export class StressTest
{
  public static readonly BASE_LEVEL = 90001;
  public static readonly COUNT = SPECS.length;

  public static readonly FIRE_INTERVAL = 0.35;

  private static active = false;
  private static autofire = false;
  private static index = 0;
  private static configs: ( LevelConfig | null )[] = SPECS.map( () => null );

  public static isActive(): boolean
  {
    return this.active;
  }

  public static isStressLevel( level: number ): boolean
  {
    return level >= this.BASE_LEVEL && level < this.BASE_LEVEL + this.COUNT;
  }

  public static getIndex(): number
  {
    return this.index;
  }

  public static getName( index: number = this.index ): string
  {
    return SPECS[( index + this.COUNT ) % this.COUNT].name;
  }

  /** Spec indices of a series, in order (each kind easy → extreme, then the next kind). */
  public static indicesOf( group: NonNullable<StressSpec["group"]> ): number[]
  {
    return SPECS.flatMap( ( spec, i ) => spec.group === group ? [ i ] : [] );
  }

  public static getBlockCount( index: number = this.index ): number
  {
    const spec = SPECS[( index + this.COUNT ) % this.COUNT];
    if ( spec.stack )
    {
      const layers = this.getConfig( this.BASE_LEVEL + ( index + this.COUNT ) % this.COUNT )?.layers ?? [];
      return layers.reduce( ( sum, layer ) => sum + ( layer.pieces?.length ?? 0 ), 0 );
    }

    const width = spec.piece?.width ?? 1;
    const height = spec.piece?.height ?? 1;
    return Math.floor( spec.columns / width ) * Math.floor( spec.rows / height ) * spec.layers;
  }

  public static indexOf( name: string ): number
  {
    return SPECS.findIndex( spec => spec.name === name );
  }

  public static currentLevel(): number
  {
    return this.BASE_LEVEL + this.index;
  }

  public static activate( index: number ): number
  {
    this.active = true;
    this.index = ( index + this.COUNT ) % this.COUNT;
    return this.currentLevel();
  }

  public static advance(): number
  {
    return this.activate( this.index + 1 );
  }

  public static deactivate(): void
  {
    this.active = false;
    this.autofire = false;
  }

  public static isAutofiring(): boolean
  {
    return this.active && this.autofire;
  }

  public static setAutofire( on: boolean ): void
  {
    this.autofire = on;
  }

  public static toggleAutofire(): boolean
  {
    this.autofire = !this.autofire;
    return this.autofire;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    if ( !this.isStressLevel( level ) )
      return null;

    const index = level - this.BASE_LEVEL;

    if ( this.configs[index] === null )
      this.configs[index] = buildConfig( index );

    return this.configs[index];
  }
}
